import { SETUP_MAX_LEAD_STAGES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Select } from "@sagot/ui";
import { newStageId, stagesValid, type StageDraft } from "./setup-config";

type Kind = "open" | "won" | "lost";
const kindOf = (stage: StageDraft): Kind => (stage.is_won ? "won" : stage.is_lost ? "lost" : "open");

/** Lead pipeline editor: rename, add, remove, reorder, open / won / lost. */
export function StageEditor({ stages, onChange }: { stages: StageDraft[]; onChange: (stages: StageDraft[]) => void }) {
  const { t } = useTranslation();
  const update = (index: number, patch: Partial<StageDraft>) => { onChange(stages.map((s, i) => (i === index ? { ...s, ...patch } : s))); };
  const move = (index: number, delta: number) => {
    const next = [...stages];
    const [item] = next.splice(index, 1);
    if (!item) return;
    next.splice(index + delta, 0, item);
    onChange(next);
  };
  const addStage = () => {
    const lostAt = stages.findIndex((s) => s.is_lost);
    const at = lostAt === -1 ? stages.length : Math.max(lostAt - 1, 1);
    const next = [...stages];
    next.splice(at, 0, { id: newStageId(), name: t("onboarding.configure.newStage"), is_won: false, is_lost: false });
    onChange(next);
  };
  const valid = stagesValid(stages);
  return (
    <fieldset className="space-y-3" data-testid="stage-editor">
      <legend className="text-sm font-bold">{t("onboarding.configure.stages")}</legend>
      <p className="text-xs text-fg-muted">{t("onboarding.configure.stagesHelp")}</p>
      <ol className="space-y-2">
        {stages.map((stage, index) => {
          const n = index + 1;
          const label = stage.name || String(n);
          return (
            <li key={stage.id} className="flex flex-wrap items-center gap-2">
              <span className="w-5 text-right text-xs tabular-nums text-fg-subtle">{n}</span>
              <Input className="min-w-0 flex-1" aria-label={t("onboarding.configure.stageName", { n })} value={stage.name} maxLength={60} onChange={(e) => { update(index, { name: e.target.value }); }} />
              <Select className="w-28" aria-label={t("onboarding.configure.stageKind", { n })} value={kindOf(stage)} onChange={(e) => { update(index, { is_won: e.target.value === "won", is_lost: e.target.value === "lost" }); }}>
                <option value="open">{t("onboarding.configure.stageOpen")}</option>
                <option value="won">{t("onboarding.configure.stageWon")}</option>
                <option value="lost">{t("onboarding.configure.stageLost")}</option>
              </Select>
              <Button variant="ghost" size="sm" aria-label={t("onboarding.configure.moveUp", { name: label })} disabled={index === 0} onClick={() => { move(index, -1); }}>
                <Icon name="chevron-up" />
              </Button>
              <Button variant="ghost" size="sm" aria-label={t("onboarding.configure.moveDown", { name: label })} disabled={index === stages.length - 1} onClick={() => { move(index, 1); }}>
                <Icon name="chevron-down" />
              </Button>
              <Button variant="ghost" size="sm" aria-label={t("onboarding.configure.removeStage", { name: label })} disabled={stages.length <= 3} onClick={() => { onChange(stages.filter((_, i) => i !== index)); }}>
                <Icon name="x" />
              </Button>
            </li>
          );
        })}
      </ol>
      <Button variant="secondary" size="sm" disabled={stages.length >= SETUP_MAX_LEAD_STAGES} onClick={addStage}>
        <Icon name="plus" />
        {t("onboarding.configure.addStage")}
      </Button>
      {valid ? null : (
        <p className="text-xs font-semibold text-danger" role="alert">
          {t("onboarding.configure.stagesInvalid")}
        </p>
      )}
    </fieldset>
  );
}
